import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { usePageMeta } from '../../lib/seo';
import { Alert, Button, Card, Field, Input, useToast } from '../../components/ui';
import { LogoMark } from '../../components/Logo';
import { tr } from '../../lib/i18n';

interface ForgotPasswordResponse {
  ok: boolean;
  message: string;
  emailConfigured: boolean;
}

export function ForgotPasswordPage(): JSX.Element {
  usePageMeta(
    tr('Esqueci minha senha'),
    tr('Recupere o acesso à sua conta AgendaPro. Seu negócio. Sua agenda. Seu tempo.'),
    '/esqueci-senha'
  );

  const navigate = useNavigate();
  const toast = useToast();

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ForgotPasswordResponse | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setResult(null);
    setFieldError(null);
    setGeneralError(null);

    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setFieldError(tr('Informe um e-mail válido.'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post<ForgotPasswordResponse>('/api/auth/forgot-password', {
        email: email.trim().toLowerCase()
      });
      setResult(res);
      toast(tr('Pedido registrado.'), 'info');
    } catch (err) {
      const message =
        err instanceof ApiError && err.message
          ? err.message
          : tr('Não foi possível processar o pedido. Tente novamente.');
      setGeneralError(message);
      toast(message, 'danger');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="mx-auto w-full max-w-content px-4 py-10 sm:py-14">
      <div className="mx-auto grid w-full max-w-md gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <LogoMark size={48} />
          <h1 className="text-3xl">{tr("Recuperar senha")}</h1>
          <p className="nl-hint">{tr("Seu negócio. Sua agenda. Seu tempo.")}</p>
        </div>

        <Card as="section">
          <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
            {generalError ? <Alert tone="danger" title={tr("Ocorreu um erro")}>{generalError}</Alert> : null}

            <Field label={tr("E-mail da conta")} hint="Informe o e-mail usado para entrar." error={fieldError} required>
              {(p) => (
                <Input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder={tr("voce@exemplo.com")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  invalid={Boolean(fieldError)}
                  {...p}
                />
              )}
            </Field>

            <Button type="submit" loading={submitting} disabled={submitting} className="w-full">
              {tr("Solicitar redefinição")}
            </Button>
          </form>
        </Card>

        {result ? (
          <div className="flex flex-col gap-3">
            <Alert tone="info" title={tr("Pedido recebido")}>
              <p>{result.message}</p>
            </Alert>

            {result.emailConfigured ? (
              <Alert tone="success" title={tr("E-mail de recuperação")}>
                <p>
                  {tr("Se existir uma conta com este e-mail, enviaremos um link de redefinição (válido por 30 minutos). Verifique sua caixa de entrada e a pasta de spam.")}
                </p>
              </Alert>
            ) : (
              <Alert tone="warning" title={tr("E-mail indisponível no momento")}>
                <p>
                  {tr("O envio do e-mail de recuperação")} <strong>{tr("não está disponível")}</strong> {tr("porque o SMTP ainda não foi configurado pelo administrador.")} <strong>{tr("Não enviamos nenhuma mensagem")}</strong>{tr(". Entre em contato com o suporte para redefinir sua senha ou tente novamente mais tarde.")}
                </p>
              </Alert>
            )}

            <div className="flex justify-center gap-2">
              <Button variant="secondary" onClick={() => navigate('/redefinir-senha')} disabled={submitting}>
                {tr("Já tenho um token")}
              </Button>
            </div>
          </div>
        ) : null}

        <p className="text-center text-sm text-muted">
          {tr("Lembrou a senha?")}{' '}
          <Link to="/entrar" className="font-medium text-graphite hover:underline">
            {tr("Entrar")}
          </Link>
        </p>
      </div>
    </section>
  );
}
